import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, MessageCircle } from "lucide-react";
import { Reveal, PageHero, CTAButton } from "@/components/bits";
import { ACTIVITIES, CATEGORIES, waLink } from "@/data/site";

const Activities = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "All";
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selected]);

  const filtered = active === "All" ? ACTIVITIES : ACTIVITIES.filter((a) => a.cat === active);

  return (
    <>
      <PageHero
        eyebrow="Activities · 14+ Categories"
        title={<>Find your <span className="text-red-500">discipline</span></>}
        sub="Dance, fitness, music and performance training under one roof — structured classes for ages 4+ to 40, across all genders. Enquire for details; no prices are listed online."
        img={undefined}
      />

      <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="activities-explorer">
        <div className="sticky top-[76px] z-30 -mx-5 flex gap-2 overflow-x-auto bg-[#050507]/95 px-5 py-4 backdrop-blur-xl sm:-mx-8 sm:px-8" role="tablist" aria-label="Activity categories">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={active === c}
              onClick={() => setParams(c === "All" ? {} : { cat: c })}
              className={`shrink-0 border px-5 py-2.5 font-display text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all ${
                active === c ? "border-red-600 bg-red-600 text-white" : "border-white/15 text-zinc-400 hover:border-red-500/60 hover:text-white"
              }`}
              data-testid={`filter-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((a, i) => (
              <motion.article
                layout
                key={a.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 8) * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className="card-glow img-zoom group flex flex-col overflow-hidden border border-white/10 bg-[#0F0F14]"
                data-testid={`activity-card-${a.slug}`}
              >
                <div className="relative overflow-hidden">
                  <img src={a.img} alt={`${a.name} classes in Patna at New Boogie Woogie Academy`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  <span className="absolute left-4 top-4 bg-black/70 px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-red-400 backdrop-blur-sm">
                    {a.cat}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg font-black uppercase leading-tight tracking-tight text-white">{a.name}</h3>
                  <p className="mt-3 flex-1 text-[13px] leading-relaxed text-zinc-400">{a.desc}</p>
                  <div className="mt-6 flex gap-2">
                    <button
                      onClick={() => setSelected(a)}
                      className="flex-1 border border-white/15 px-4 py-2.5 font-display text-[10px] font-extrabold uppercase tracking-[0.14em] text-white transition-colors hover:border-red-500 hover:text-red-400"
                      data-testid={`activity-learn-${a.slug}`}
                    >
                      Learn More
                    </button>
                    <Link
                      to={`/booking?activity=${encodeURIComponent(a.name)}`}
                      className="flex-1 bg-gradient-to-r from-red-600 to-rose-700 px-4 py-2.5 text-center font-display text-[10px] font-extrabold uppercase tracking-[0.14em] text-white transition-all hover:brightness-110"
                      data-testid={`activity-enquire-${a.slug}`}
                    >
                      Enquire Now
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-16 text-center">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-zinc-500">Enquire for details · No prices listed online</p>
          <CTAButton href={waLink("Hello New Boogie Woogie Academy, I would like to know more about your classes and batches.")} variant="whatsapp" className="mt-6" testid="activities-whatsapp-btn">
            <MessageCircle size={15} /> Ask on WhatsApp
          </CTAButton>
        </Reveal>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-5 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            role="dialog"
            aria-modal="true"
            aria-label={selected.name}
            data-testid="activity-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[88vh] w-full max-w-2xl overflow-y-auto border border-white/12 bg-[#0F0F14]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img src={selected.img} alt={`${selected.name} at New Boogie Woogie Academy`} className="aspect-[16/9] w-full object-cover" />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur-sm transition-colors hover:border-red-500"
                  aria-label="Close"
                  data-testid="activity-modal-close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-8">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-red-500">{selected.cat}</span>
                <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{selected.name}</h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{selected.desc}</p>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                  Classes are conducted in a structured, organized manner with a focus on technique, rhythm,
                  coordination, expression, and confidence. Open to ages 4+ to 40, across all genders. Contact us
                  for batch timings and enrolment details.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <CTAButton href={`/booking?activity=${encodeURIComponent(selected.name)}`} testid="activity-modal-enquire">
                    Enquire Now <ArrowRight size={15} />
                  </CTAButton>
                  <CTAButton href={waLink(`Hello New Boogie Woogie Academy, I would like to enquire about ${selected.name} classes.`)} variant="whatsapp" testid="activity-modal-whatsapp">
                    <MessageCircle size={15} /> WhatsApp Us
                  </CTAButton>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Activities;
